import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Plus, Trash2, Building2, Pencil, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePlanLimit, PlanLimitBanner } from "@/components/PlanLimitGuard";
import { TID } from "@/constants/testIds";

const EMPTY = {
  name: "",
  code: "",
  city: "",
  state: "",
  address: "",
  latitude: "",
  longitude: "",
  geofence_radius_m: "",
};

export default function Units() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const limitInfo = usePlanLimit("units");

  const load = async () => {
    try {
      const { data } = await api.get("/units");
      setItems(data);
    } catch (err) {
      console.error("Failed to load units", err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name || "",
      code: u.code || "",
      city: u.city || "",
      state: u.state || "",
      address: u.address || "",
      latitude: u.latitude ?? "",
      longitude: u.longitude ?? "",
      geofence_radius_m: u.geofence_radius_m ?? "",
    });
    setOpen(true);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocalização não suportada neste navegador");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setForm((f) => ({
          ...f,
          latitude: Number(pos.coords.latitude.toFixed(6)),
          longitude: Number(pos.coords.longitude.toFixed(6)),
          geofence_radius_m: f.geofence_radius_m || 200,
        })),
      () => toast.error("Não foi possível obter sua localização"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      latitude: form.latitude === "" ? null : Number(form.latitude),
      longitude: form.longitude === "" ? null : Number(form.longitude),
      geofence_radius_m:
        form.geofence_radius_m === "" ? null : Number(form.geofence_radius_m),
    };
    try {
      if (editing) {
        await api.patch(`/units/${editing.id}`, payload);
        toast.success("Unidade atualizada");
      } else {
        await api.post("/units", payload);
        toast.success("Unidade criada");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    }
  };

  const remove = async (id) => {
    if (!confirm("Excluir esta unidade?")) return;
    try {
      await api.delete(`/units/${id}`);
      toast.success("Unidade removida");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao excluir");
    }
  };

  return (
    <Layout
      title="Unidades"
      subtitle="Gerencie as unidades operacionais da sua empresa"
      actions={
        <Button
          className="bg-emerald-800 hover:bg-emerald-900 text-white"
          data-testid={TID.unitsNew}
          onClick={() => {
            if (limitInfo.reached) {
              toast.error(
                `Limite do plano atingido (${limitInfo.limit} unidades). Faça upgrade em Planos & Cobrança.`
              );
              return;
            }
            openCreate();
          }}
          disabled={limitInfo.reached}
        >
          <Plus className="h-4 w-4 mr-2" /> Nova unidade
        </Button>
      }
    >
      <PlanLimitBanner info={limitInfo} label="unidade(s)" />
      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setEditing(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? `Editar unidade — ${editing.name}` : "Nova unidade"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-1 bg-white" data-testid="unit-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Código</Label>
                <Input value={form.code} onChange={(e) => set("code", e.target.value)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label>Cidade</Label>
                <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1 bg-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Estado</Label>
                <Input value={form.state} onChange={(e) => set("state", e.target.value)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label>Endereço</Label>
                <Input value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1 bg-white" />
              </div>
            </div>
            {/* FASE 20 — geofence */}
            <div className="border border-stone-200 rounded-md p-3 bg-stone-50/40 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-body text-sm font-semibold">Localização da loja (geofence)</Label>
                  <p className="text-[11px] text-stone-500 font-body">
                    Checklists executados fora do raio serão sinalizados ao gestor.
                  </p>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={useMyLocation} data-testid="unit-use-location">
                  <MapPin className="h-3.5 w-3.5 mr-1" /> Usar minha localização
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Latitude</Label>
                  <Input type="number" step="any" value={form.latitude} onChange={(e) => set("latitude", e.target.value)} className="mt-1 bg-white" data-testid="unit-latitude" />
                </div>
                <div>
                  <Label className="text-xs">Longitude</Label>
                  <Input type="number" step="any" value={form.longitude} onChange={(e) => set("longitude", e.target.value)} className="mt-1 bg-white" data-testid="unit-longitude" />
                </div>
                <div>
                  <Label className="text-xs">Raio (metros)</Label>
                  <Input type="number" min="10" value={form.geofence_radius_m} onChange={(e) => set("geofence_radius_m", e.target.value)} placeholder="200" className="mt-1 bg-white" data-testid="unit-radius" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid={TID.unitsSubmit}>
                {editing ? "Salvar alterações" : "Criar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card data-testid={TID.units} className="rounded-xl border-stone-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-body">Unidade</TableHead>
              <TableHead className="font-body">Código</TableHead>
              <TableHead className="font-body">Cidade/UF</TableHead>
              <TableHead className="font-body">Endereço</TableHead>
              <TableHead className="font-body text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-stone-500 py-10">
                  <Building2 className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                  Nenhuma unidade cadastrada
                </TableCell>
              </TableRow>
            )}
            {items.map((u) => (
              <TableRow key={u.id}>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => openEdit(u)}
                    className="font-display font-semibold text-emerald-900 hover:underline inline-flex items-center gap-1.5"
                    data-testid={`unit-edit-${u.id}`}
                  >
                    {u.name}
                    <Pencil className="h-3 w-3 opacity-40" />
                  </button>
                </TableCell>
                <TableCell className="font-body text-sm text-stone-500">{u.code || "—"}</TableCell>
                <TableCell className="font-body text-sm">
                  {u.city || "—"} {u.state ? `/ ${u.state}` : ""}
                </TableCell>
                <TableCell className="font-body text-sm text-stone-500">{u.address || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => remove(u.id)} data-testid={`unit-delete-${u.id}`}>
                    <Trash2 className="h-4 w-4 text-rose-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Layout>
  );
}
